import ReassignIcon from "@mui/icons-material/CallSplitRounded";
import CheckIcon from "@mui/icons-material/CheckRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import CancelIcon from "@mui/icons-material/ContentPasteOffRounded";
import { Button, Stack } from "@mui/material";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import type { Approval } from "../../api/types/approvalTypes";
import useUserContext from "../../context/useUserContext";
import { getInitialApproversAssignmentStateFromPendingApproval } from "./workflows/approversAssignmentState";

interface Props {
  approval: Approval;
  onActionRequest: (action: "approve" | "reject" | "cancel" | "reassign") => void;
  hideSecondaryActions?: boolean;
}

const ApprovalActions = ({ approval, onActionRequest, hideSecondaryActions }: Props) => {
  const { id: userId } = useUserContext();

  if (approval.status !== "Pending") {
    return null;
  }

  const isUserAllowedToApproveOrReject = approval.pendingApprovalsFromUserIds.includes(userId);

  const isUserAllowedToReassign =
    isUserAllowedToApproveOrReject &&
    getInitialApproversAssignmentStateFromPendingApproval(approval, userId).steps.length > 0;

  const isUserAllowedToCancel = approval.createdBy === userId;

  if (isUserAllowedToApproveOrReject) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Button variant="outlined" color="success" startIcon={<CheckIcon />} onClick={() => onActionRequest("approve")}>
          Approve
        </Button>
        <Button variant="outlined" color="error" startIcon={<CloseIcon />} onClick={() => onActionRequest("reject")}>
          Reject
        </Button>
        {!hideSecondaryActions && (isUserAllowedToReassign || isUserAllowedToCancel) && (
          <ActionsMenuButton
            items={[
              {
                label: "Reassign",
                icon: <ReassignIcon />,
                onClick: () => onActionRequest("reassign"),
                hidden: !isUserAllowedToReassign,
              },
              {
                label: "Cancel",
                icon: <CancelIcon />,
                onClick: () => onActionRequest("cancel"),
                hidden: !isUserAllowedToCancel,
              },
            ]}
          />
        )}
      </Stack>
    );
  }

  if (!hideSecondaryActions && (isUserAllowedToReassign || isUserAllowedToCancel)) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        {isUserAllowedToReassign && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ReassignIcon />}
            onClick={() => onActionRequest("reassign")}
          >
            Reassign
          </Button>
        )}
        {isUserAllowedToCancel && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<CancelIcon />}
            onClick={() => onActionRequest("cancel")}
          >
            Cancel
          </Button>
        )}
      </Stack>
    );
  }

  return null;
};

export default ApprovalActions;
